import { ReactNode } from "react";
import { cn } from "../utls/utils";

type UnreadBadgeTone = "primary" | "neutral" | "success";

export interface UnreadBadgeProps {
  /**
   * Number of unread items. If omitted, the badge renders as a simple dot.
   */
  count?: number;
  /**
   * Maximum value to display before switching to the `99+` style indicator.
   */
  maxCount?: number;
  /**
   * Forces the badge to render a dot indicator regardless of the `count` value.
   */
  dot?: boolean;
  /**
   * Display style that maps to Harper theme color tokens.
   */
  tone?: UnreadBadgeTone;
  /**
   * When false, the badge hides automatically when `count` is 0 or undefined.
   */
  showZero?: boolean;
  /**
   * Optional accessible label. When omitted a label is generated.
   */
  ariaLabel?: string;
  /**
   * Optional content rendered inside the badge when in dot mode.
   */
  children?: ReactNode;
  className?: string;
}

const toneStyles: Record<UnreadBadgeTone, string> = {
  primary: "bg-terracotta-500 text-white",
  neutral: "bg-beige-300 text-terracotta-800",
  success: "bg-emerald-500 text-white",
};

const dotToneStyles: Record<UnreadBadgeTone, string> = {
  primary: "bg-terracotta-500",
  neutral: "bg-beige-400",
  success: "bg-emerald-500",
};

export function UnreadBadge({
  count,
  maxCount = 99,
  dot,
  tone = "primary",
  showZero = false,
  ariaLabel,
  children,
  className,
}: UnreadBadgeProps) {
  const numericCount = count ?? 0;
  const isDot = dot ?? (count === undefined);
  const isEmpty = !showZero && (count === undefined || numericCount <= 0);

  if (!isDot && isEmpty) {
    return null;
  }

  const displayValue = !isDot
    ? numericCount > maxCount
      ? `${maxCount}+`
      : numericCount.toString()
    : undefined;

  const resolvedAriaLabel =
    ariaLabel ??
    (isDot
      ? "Unread items"
      : `${displayValue ?? numericCount} unread notification${numericCount === 1 ? "" : "s"}`);

  if (isDot) {
    return (
      <span
        role="status"
        aria-label={resolvedAriaLabel}
        className={cn(
          "inline-flex items-center justify-center rounded-full",
          dotToneStyles[tone],
          "h-2.5 w-2.5",
          className
        )}
      >
        {children}
      </span>
    );
  }

  return (
    <span
      role="status"
      aria-label={resolvedAriaLabel}
      className={cn(
        "inline-flex min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-semibold",
        toneStyles[tone],
        className
      )}
    >
      {displayValue}
    </span>
  );
}

export default UnreadBadge;
